import { AgentSessionSummary, PagingMeta } from "@/types/agent"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "./redux-store"

export type AgentSessionsListFilter =
  | "all"
  | "ops_agent_shared"
  | "cs_agent_shared"
  | "admin_booking_agent_shared"

export interface AgentSessionsListSliceState {
  sessions: AgentSessionSummary[]
  loading: boolean
  error: string | null
  currentPage: number
  pagingMeta: PagingMeta | null
  includeEvalSessions: boolean
  sessionFilter: AgentSessionsListFilter
}

export const agentSessionsListSliceInitialState: AgentSessionsListSliceState = {
  sessions: [],
  loading: true,
  error: null,
  currentPage: 1,
  pagingMeta: null,
  includeEvalSessions: false,
  sessionFilter: "all",
}

const agentSessionsListSlice = createSlice({
  name: "agentSessionsListSlice",
  initialState: agentSessionsListSliceInitialState,
  reducers: {
    setSessions: (state, action: PayloadAction<AgentSessionSummary[]>) => {
      state.sessions = action.payload
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },
    setPagingMeta: (state, action: PayloadAction<PagingMeta | null>) => {
      state.pagingMeta = action.payload
    },
    updateSession: (state, action: PayloadAction<AgentSessionSummary>) => {
      const index = state.sessions.findIndex((s) => s.id === action.payload.id)
      if (index !== -1) {
        state.sessions[index] = action.payload
      }
    },
    // Batch version: applies multiple session updates in a single Immer pass
    // so React sees one state transition instead of N.
    updateSessionBatch: (
      state,
      action: PayloadAction<AgentSessionSummary[]>
    ) => {
      for (const session of action.payload) {
        const index = state.sessions.findIndex((s) => s.id === session.id)
        if (index !== -1) {
          state.sessions[index] = session
        }
      }
    },
    setIncludeEvalSessions: (state, action: PayloadAction<boolean>) => {
      state.includeEvalSessions = action.payload
    },
    setSessionFilter: (
      state,
      action: PayloadAction<AgentSessionsListFilter>
    ) => {
      state.sessionFilter = action.payload
    },
  },
})

const selectSliceState = (state: RootState) => state.agentSessionsListSlice

export const selectAgentSessions = createSelector(
  [selectSliceState],
  (state) => state.sessions
)

export const selectAgentSessionsLoading = createSelector(
  [selectSliceState],
  (state) => state.loading
)

export const selectAgentSessionsError = createSelector(
  [selectSliceState],
  (state) => state.error
)

export const selectAgentSessionsCurrentPage = createSelector(
  [selectSliceState],
  (state) => state.currentPage
)

export const selectAgentSessionsPagingMeta = createSelector(
  [selectSliceState],
  (state) => state.pagingMeta
)

export const selectAgentSessionsHasNextPage = createSelector(
  [selectSliceState],
  (state) => state.pagingMeta?.next_page !== null
)

export const selectAgentSessionsHasPrevPage = createSelector(
  [selectSliceState],
  (state) => state.pagingMeta?.prev_page !== null
)

export const selectAgentSessionsIncludeEvalSessions = createSelector(
  [selectSliceState],
  (state) => state.includeEvalSessions
)

export const selectAgentSessionsFilter = createSelector(
  [selectSliceState],
  (state) => state.sessionFilter
)

export const {
  setSessions,
  setLoading,
  setError,
  setCurrentPage,
  setPagingMeta,
  updateSession,
  updateSessionBatch,
  setIncludeEvalSessions,
  setSessionFilter,
} = agentSessionsListSlice.actions

export default agentSessionsListSlice
